// noinspection SpellCheckingInspection

import React from 'react';
import { useNavigate } from 'react-router-dom';

import AccountBoxIcon from '@mui/icons-material/AccountBox';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import SettingsIcon from '@mui/icons-material/Settings';
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import MenuItem from "@mui/material/MenuItem";
import MenuList from "@mui/material/MenuList";
import Popover from "@mui/material/Popover";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";

import { AccountType } from 'common/values/account-type/account-type';
import AccountSwitcher from 'common/values/account-type/view/account-switcher';

import { useSession } from 'users/session/session-context';
import UserEntityInfo from 'users/values/user-entity-info/user-entity-info';
import { enqueueSnackbar } from 'notistack';

const MenuContainer = styled('div')(({ theme }) => ({
  minWidth: theme.spacing(40)
}));
const UserAvatar = styled(Avatar)<{ accounttype: AccountType }>(({ theme, accounttype }) => ({
  backgroundColor: (accounttype === AccountType.Client) ? '#E19941' : '#404558',
  color: '#FFF',
  fontSize: '1em',
  height: theme.spacing(6),
  width: theme.spacing(6),
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.66em',
    height: theme.spacing(4),
    width: theme.spacing(4),
  }
}));
const UserInfoContainer = styled(Box)(({ theme }) => ({
  alignItems: 'center',
  display: 'flex',
  gap: theme.spacing(1.5),
  padding: theme.spacing(1, 2)
}));
const OptionsMenu = styled(MenuList)(({ theme }) => ({
  padding: theme.spacing(0, 0, 1, 0)
}));

export default function UserMenu() {
  const [avatarMenuAnchor, setAvatarMenuAnchor] = React.useState<Element>();

  const navigate = useNavigate();
  const session = useSession();

  async function handleLogoutClick() {
    setAvatarMenuAnchor(undefined);
    await session.logout();
    navigate('/login');
  }

  return (
    <React.Fragment>
      <IconButton
        aria-haspopup="true"
        onClick={(event) => setAvatarMenuAnchor(event.currentTarget)}>
        <UserAvatar accounttype={session.accountType}>
          {session.user?.initials}
        </UserAvatar>
      </IconButton>
      <Popover
        anchorEl={avatarMenuAnchor}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
        open={Boolean(avatarMenuAnchor)}
        onClose={() => setAvatarMenuAnchor(undefined)}>
        <MenuContainer>
          {session.canSwitchContext && (
            <>
              <AccountSwitcher />
              <Divider />
            </>
          )}
          <UserInfoContainer>
            <Avatar />
            <div>
              <Typography variant="h6">{session.user?.fullName}</Typography>
              {session.entities?.map((entity: UserEntityInfo) => (
                <Typography key={entity.entityId.value} variant="body2">{entity.entityName.value}</Typography>
              ))}
            </div>
          </UserInfoContainer>
          <OptionsMenu>
            <MenuItem onClick={() => {
              setAvatarMenuAnchor(undefined);
              navigate('/marketplace/profiles');
            }}>
              <ListItemIcon>
                <AccountBoxIcon />
              </ListItemIcon>
              <ListItemText primary="My Profiles" />
            </MenuItem>
            <MenuItem onClick={() => {
              setAvatarMenuAnchor(undefined);
              enqueueSnackbar('Settings page is not implemented yet', { variant: 'info' });
            }}>
              <ListItemIcon>
                <SettingsIcon />
              </ListItemIcon>
              <ListItemText primary="Settings" />
            </MenuItem>
            <MenuItem onClick={handleLogoutClick}>
              <ListItemIcon>
                <ExitToAppIcon />
              </ListItemIcon>
              <ListItemText primary="Logout" />
            </MenuItem>
          </OptionsMenu>
        </MenuContainer>
      </Popover>
    </React.Fragment >
  );
}
